import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

function CountBadge({ count, active }) {
  if (count === undefined || count === null) return null;
  return (
    <span className={`px-1.5 py-0.5 text-[11px] leading-none rounded-full font-mono font-semibold ${active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200/70 text-slate-600"}`}>
      {count}
    </span>
  );
}

// Onglets de section unifiés (pro + admin). variant "primary" = barre soulignée, "secondary" = pilules segmentées.
export function SectionTabs({ tabs, active, onChange, variant = "primary", testIdPrefix = "tab", className = "" }) {
  const scrollRef = useRef(null);
  const [overflow, setOverflow] = useState({ left: false, right: false });

  const updateOverflow = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setOverflow({ left: el.scrollLeft > 4, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4 });
  }, []);

  useEffect(() => {
    updateOverflow();
    const el = scrollRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(updateOverflow);
    ro.observe(el);
    el.addEventListener("scroll", updateOverflow, { passive: true });
    return () => { ro.disconnect(); el.removeEventListener("scroll", updateOverflow); };
  }, [updateOverflow, tabs.length]);

  useEffect(() => {
    const el = scrollRef.current?.querySelector(`[data-tab-id="${active}"]`);
    el?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [active]);

  const scrollBy = (dir) => scrollRef.current?.scrollBy({ left: dir * 240, behavior: "smooth" });
  const isPrimary = variant === "primary";

  return (
    <div className={`relative ${className}`} data-testid={`${testIdPrefix}-bar`}>
      {overflow.left && (
        <>
          <div className="pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-white to-transparent z-10" />
          <button type="button" onClick={() => scrollBy(-1)} aria-label="Onglets précédents" data-testid={`${testIdPrefix}-scroll-left`}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-7 h-7 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center text-slate-600 hover:bg-slate-50">
            <ChevronLeft className="w-4 h-4" />
          </button>
        </>
      )}
      {overflow.right && (
        <>
          <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-white to-transparent z-10" />
          <button type="button" onClick={() => scrollBy(1)} aria-label="Onglets suivants" data-testid={`${testIdPrefix}-scroll-right`}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-7 h-7 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center text-slate-600 hover:bg-slate-50">
            <ChevronRight className="w-4 h-4" />
          </button>
        </>
      )}
      <div
        ref={scrollRef}
        className={isPrimary
          ? "flex items-end gap-1 overflow-x-auto scrollbar-none scroll-smooth border-b border-slate-200"
          : "inline-flex max-w-full p-1 gap-1 bg-slate-100/80 border border-slate-200 rounded-xl overflow-x-auto scrollbar-none scroll-smooth"}
      >
        {tabs.map((tab) => {
          const isActive = active === tab.id;
          const Icon = tab.icon;
          const base = isPrimary
            ? `shrink-0 whitespace-nowrap flex items-center gap-2 py-2.5 px-4 text-sm rounded-t-lg border-b-2 -mb-px transition-colors ${isActive ? "font-semibold text-emerald-800 border-primary bg-emerald-50/50" : "font-medium text-slate-600 border-transparent hover:text-slate-900 hover:bg-slate-100/70"}`
            : `shrink-0 whitespace-nowrap flex items-center gap-2 px-3 py-1.5 text-xs sm:text-sm rounded-lg transition-colors ${isActive ? "font-semibold text-slate-900 bg-white shadow-sm" : "font-medium text-slate-600 hover:text-slate-900"}`;
          return (
            <button
              key={tab.id}
              type="button"
              data-tab-id={tab.id}
              onClick={() => onChange(tab.id)}
              data-testid={tab.testId || `${testIdPrefix}-${tab.id}`}
              aria-current={isActive ? "page" : undefined}
              className={`${base} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1`}
            >
              {Icon && <Icon className="w-4 h-4 shrink-0" />}
              {tab.label}
              <CountBadge count={tab.count} active={isActive} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default SectionTabs;
